<script lang="ts" setup>
import { cva } from 'class-variance-authority'
import { DialogContent, DialogOverlay, DialogPortal, DialogRoot } from 'reka-ui'
import { computed, type HTMLAttributes } from 'vue'

import { cn } from '../../../..'

const contentVariants = cva('atabix-dialog-content', {
  variants: {
    variant: {
      default: 'border-border',
      error: 'border-border-alert',
      info: 'border-border-info',
    },
  },
})

// Props
export interface DialogBaseProperties {
  class: HTMLAttributes['class']
  type?: string
  title: string
  description?: string
}

const properties = withDefaults(defineProps<DialogBaseProperties>(), {
  type: 'default',
  description: undefined,
})

// end Props

// Emits
export interface DialogBaseEmits {
  (event: 'clickOutsideOrKeyupEscape'): void
}

const emit = defineEmits<DialogBaseEmits>()

// end Emits

// Refs
// end Refs

// Computed
const computedStyle = computed(() => {
  switch (properties.type) {
    case 'error': {
      return cn(contentVariants({ variant: 'error' }), properties.class)
    }
    case 'info': {
      return cn(contentVariants({ variant: 'info' }), properties.class)
    }
    default: {
      return cn(contentVariants({ variant: 'default' }), properties.class)
    }
  }
})
// end Computed

// Functions
function onClickOutsideOrKeyupEscape(): void {
  emit('clickOutsideOrKeyupEscape')
}
// end Functions

// Async Api functions
// end Async Api functions

// Lifecycle Hooks / Init
// end Lifecycle Hooks / Init
</script>

<template>
  <DialogRoot open @update:open="onClickOutsideOrKeyupEscape">
    <slot name="portal">
      <DialogPortal>
        <DialogOverlay class="atabix-dialog-overlay" />

        <slot name="content">
          <DialogContent :class="computedStyle" :description="description || title">
            <slot name="default" />
          </DialogContent>
        </slot>
      </DialogPortal>
    </slot>
  </DialogRoot>
</template>

<style lang="scss">
.atabix-dialog-overlay {
  @apply z-20;
  @apply bg-background-overlay-70;
  @apply fixed;
  @apply inset-0;
}

.atabix-dialog-content {
  @apply z-20;
  @apply outline-none;
  @apply left-1/2;
  @apply top-1/2;
  @apply bg-background;
  @apply text-foreground;
  @apply fixed;
  @apply -translate-x-1/2;
  @apply -translate-y-1/2;
  @apply rounded-md;
  @apply shadow;
  @apply border;
}
</style>
